import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Bell, Calendar, Clock, Loader2, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, CheckCircle2, Mail, MailOpen, CheckCheck } from 'lucide-react';
import { notificationsAPI, getErrorMessage } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';

const NotificationsContent = () => {
  // State for list view
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [pageSize] = useState(20);
  
  // State for detail view
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // State for unread count (from server)
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // Fetch unread count from server
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      const data = response.data?.data || {};
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  // Fetch notifications list
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await notificationsAPI.getNotifications({
        page: currentPage,
        page_size: pageSize,
      });
      
      const responseData = response.data;
      const notificationsData = responseData?.data || [];
      const pagination = responseData?.pagination || {};
      
      setNotifications(notificationsData);
      setTotalNotifications(pagination?.total || notificationsData.length);
      
      // Also refresh unread count
      fetchUnreadCount();
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, fetchUnreadCount]);

  // Initial load
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);
    try {
      const response = await notificationsAPI.markAllAsRead();
      const data = response.data?.data || {};
      
      toast.success({
        title: 'All Notifications Read',
        description: `Marked ${data.marked_count || 0} notification(s) as read.`,
      });
      
      // Refresh notifications and unread count
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      toast.error({
        title: 'Error',
        description: getErrorMessage(err) || 'Failed to mark notifications as read.',
      });
    } finally {
      setMarkingAllRead(false);
    }
  };

  // Fetch notification detail and mark as read
  const handleNotificationClick = async (notification) => {
    setDetailLoading(true);
    
    try {
      // Fetch full notification details
      const response = await notificationsAPI.getNotificationDetail(notification.id);
      const notificationDetail = response.data?.data || notification;
      
      setSelectedNotification(notificationDetail);
      
      // Mark as read if not already read
      if (!notification.is_read) {
        try {
          await notificationsAPI.markAsRead(notification.id);
          // Update local state to reflect the change
          setNotifications(prev => prev.map(n => 
            n.id === notification.id ? { ...n, is_read: true } : n
          ));
          // Decrease unread count
          setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (markErr) {
          console.error('Failed to mark notification as read:', markErr);
        }
      }
    } catch (err) {
      console.error('Failed to fetch notification detail:', err);
      toast.error({
        title: 'Error',
        description: getErrorMessage(err) || 'Failed to load notification details.',
      });
      // Fallback to basic notification data
      setSelectedNotification(notification);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedNotification(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Calculate read count from notifications list
  const readCount = notifications.filter(n => n.is_read).length;
  const totalPages = Math.ceil(totalNotifications / pageSize);

  // Detail View
  if (selectedNotification) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
        >
          <ArrowLeft size={18} />
          Back to Notifications
        </button>

        {/* Detail Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 p-8 border-r border-gray-100">
              {/* Title Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    <Bell size={12} />
                    Notification
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                    <CheckCircle2 size={12} />
                    Read
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">{selectedNotification.title}</h1>
              </div>

              {/* Message Content */}
              <div className="prose prose-gray max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
                  {selectedNotification.message}
                </div>
              </div>

              {/* Image if available */}
              {selectedNotification.image_url && (
                <div className="mt-6">
                  <img 
                    src={selectedNotification.image_url} 
                    alt="Notification" 
                    className="rounded-lg max-w-full h-auto shadow-md"
                  />
                </div>
              )}
            </div>

            {/* Right Column - Notification Info */}
            <div className="lg:col-span-1 bg-gray-50 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Bell size={20} className="text-blue-600" />
                Notification Details
              </h3>
              
              <div className="space-y-5">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar size={14} />
                    Received Date
                  </div>
                  <p className="font-semibold text-gray-900 text-lg">
                    {formatDate(selectedNotification.sent_at || selectedNotification.created_at)}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Clock size={14} />
                    Received Time
                  </div>
                  <p className="font-semibold text-gray-900 text-lg">
                    {formatTime(selectedNotification.sent_at || selectedNotification.created_at)}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Mail size={14} />
                    Status
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    <CheckCircle2 size={14} />
                    {selectedNotification.status === 'sent' ? 'Delivered' : 'Received'}
                  </span>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    Notification Type
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-semibold">
                    {selectedNotification.type === 'admin_push' ? 'Admin Update' : 'System'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load notifications</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchNotifications}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Bell size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Notifications</h2>
              <p className="text-blue-100 text-sm">Stay updated with important announcements</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium text-sm"
              >
                <CheckCheck size={16} className={markingAllRead ? 'animate-pulse' : ''} />
                {markingAllRead ? 'Marking...' : 'Mark All Read'}
              </button>
            )}
            <button
              onClick={fetchNotifications}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium text-sm"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Notifications</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{totalNotifications}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Bell className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-5 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Unread</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{unreadCount}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Mail className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Read</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{readCount}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <MailOpen className="text-green-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {detailLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <Loader2 className="animate-spin text-blue-600" size={32} />
          </div>
        )}
        
        {notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg font-medium">No notifications yet</p>
            <p className="text-gray-400 text-sm mt-1">You'll see important updates here</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const notificationIsRead = notification.is_read;
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-5 hover:bg-blue-50 cursor-pointer transition-all ${
                      !notificationIsRead ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Unread indicator */}
                      <div className="flex-shrink-0 mt-1">
                        {!notificationIsRead ? (
                          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        ) : (
                          <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4 mb-1">
                          <h3 className={`font-semibold truncate ${
                            !notificationIsRead ? 'text-gray-900' : 'text-gray-600'
                          }`}>
                            {notification.title}
                          </h3>
                          <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                            <Calendar size={12} />
                            <span>{formatDate(notification.sent_at || notification.created_at)}</span>
                            <Clock size={12} />
                            <span>{formatTime(notification.sent_at || notification.created_at)}</span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm line-clamp-2">
                          {notification.message}
                        </p>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="text-gray-400 flex-shrink-0" size={20} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t p-4 flex items-center justify-between bg-gray-50">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages} ({totalNotifications} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Wrapper component with access control
const Notifications = () => {
  return (
    <UpgradeOverlay service="notifications">
      <NotificationsContent />
    </UpgradeOverlay>
  );
};

export default Notifications;
